// src/components/EducaHero.jsx
// Hero plein-largeur EDUCA — IA en carré diagonal (haut-gauche), logo central intact.
// Banner “Officiellement en développement” mis en avant en haut.
// Mise à jour "DocMod-safe" : texte plus lisible (contrasté) et chips plus opaques en dark/reader.

export default function EducaHero() {
  const BRAND_PRIMARY = "#013486"; // Bleu EDUCA
  const BRAND_ACCENT  = "#F35403"; // Orange EDUCA

  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-br from-[#f4f7ff] to-white dark:from-[#071130] dark:to-[#050b1f]">
      {/* Glows de marque */}
      <div
        className="pointer-events-none absolute -inset-40 opacity-30 blur-3xl"
        style={{
          background: `
            radial-gradient(40% 30% at 20% 15%, ${BRAND_PRIMARY}26, transparent 60%),
            radial-gradient(35% 28% at 85% 20%, ${BRAND_ACCENT}22, transparent 60%)
          `,
        }}
      />
      {/* Motif discret */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04] dark:opacity-[0.07]"
        style={{
          background:
            "radial-gradient(circle at 25% 20%, #000 2px, transparent 2px) 0 0/22px 22px",
        }}
      />

      {/* Banner haut : “Officiellement en développement” */}
      <div className="relative z-30 flex justify-center">
        <div
          role="status"
          aria-live="polite"
          className="
            mt-4 inline-flex items-center gap-3 rounded-2xl
            bg-amber-500/15 px-4 py-2 text-sm font-semibold
            text-amber-800 ring-1 ring-amber-400/40 shadow-md
            transition-all hover:scale-[1.02]
            sm:mt-6 sm:px-5 sm:py-2.5 sm:text-base
            dark:text-amber-200 dark:ring-amber-300/40
          "
        >
          <span className="h-2 w-2 rounded-full bg-current animate-pulse" />
          Officiellement en développement
        </div>
      </div>

      {/* Mot EDUCA en arrière-plan */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 mx-auto select-none text-center font-semibold tracking-[0.18em] text-[#0b1f4d]/10 dark:text-white/10"
        style={{ fontSize: "18vw", lineHeight: 1 }}
      >
        EDUCA
      </div>

      {/* Contenu */}
      <div className="relative mx-auto flex min-h-[560px] max-w-[1200px] flex-col items-center px-4 py-12 sm:px-8 md:flex-row md:items-center md:py-16">
        {/* Colonne gauche */}
        <div className="z-10 w-full md:w-1/3">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-black/5 px-3 py-1 text-xs font-medium text-black/80 ring-1 ring-black/10 dark:bg-white/10 dark:text-white dark:ring-white/15">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              Propulsée par l’IA
            </span>
            {/* ancien chip “Officiellement en développement” retiré ici */}
          </div>

          <h1 className="text-3xl font-semibold leading-tight text-[#0b1f4d] dark:text-white sm:text-4xl">
            EDUCA — l’espace numérique de votre école
          </h1>
          <p className="mt-3 max-w-md text-sm text-zinc-700 dark:text-zinc-200">
            Gestion claire & accompagnement pédagogique, <span className="font-medium">propulsés par l’IA</span>.
          </p>

          {/* Carte info "Votre espace d’école" — contraste renforcé */}
          <div className="mt-6 w-full max-w-sm rounded-3xl border border-black/10 bg-white/80 px-5 py-4 shadow-md backdrop-blur-sm dark:border-white/15 dark:bg-white/[.06]">
            <div className="flex items-center gap-3">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: BRAND_ACCENT }} />
              <div className="text-base sm:text-lg font-semibold text-[#0b1f4d] dark:text-white">
                L’espace numérique de votre école
              </div>
            </div>
            <div className="mt-2 text-sm sm:text-[15px] leading-relaxed text-zinc-700 dark:text-zinc-200">
              Gestion centralisée, pédagogie assistée par l’IA, résultats mesurables.
            </div>
          </div>
        </div>

        {/* Centre : deux carrés diagonaux (IA en petit en haut-gauche + logo EDUCA au centre) */}
        <div className="relative z-10 order-2 my-10 flex w-full justify-center md:order-none md:my-0 md:w-1/3">
          <div className="relative">
            {/* Carré principal : LOGO EDUCA */}
            <div className="relative flex h-[260px] w-[260px] items-center justify-center rounded-[32px] border border-black/10 bg-white/70 shadow-xl backdrop-blur-md dark:border-white/10 dark:bg-white/10 sm:h-[280px] sm:w-[280px] md:h-[320px] md:w-[320px] lg:h-[340px] lg:w-[340px]">
              {/* Halo interne de marque */}
              <div
                className="pointer-events-none absolute -inset-6 blur-2xl opacity-40"
                style={{
                  background: `
                    radial-gradient(60% 40% at 50% 30%, ${BRAND_PRIMARY}22, transparent),
                    radial-gradient(50% 35% at 60% 70%, ${BRAND_ACCENT}22, transparent)
                  `,
                }}
              />
              {/* LOGO EDUCA */}
              <img
                src="/images/educa.png"
                alt="Logo EDUCA"
                className="relative z-10 h-[52%] w-[52%] object-contain drop-shadow-[0_8px_24px_rgba(1,52,134,0.25)] md:h-[56%] md:w-[56%]"
              />
            </div>

            {/* Petit carré IA : en haut-gauche, ~1/2 du principal */}
            <div className="absolute left-0 top-0 z-20 -translate-x-1/4 -translate-y-1/4 sm:-translate-x-1/3 sm:-translate-y-1/3">
              <div className="flex h-[120px] w-[120px] items-center justify-center rounded-[24px] border border-black/10 bg-white/80 shadow-lg backdrop-blur-md dark:border-white/15 dark:bg-white/[.06] sm:h-[130px] sm:w-[130px] md:h-[160px] md:w-[160px] lg:h-[170px] lg:w-[170px]">
                <img
                  src="/images/ai.png"
                  alt="Logo IA"
                  className="h-[70%] w-[70%] object-contain opacity-90"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Panneau droit — DocMod safe */}
        <div className="z-10 w-full md:w-1/3">
          {/* Animations légères */}
          <style>{`
            @keyframes float { 0%{transform:translateY(0)} 50%{transform:translateY(-4px)} 100%{transform:translateY(0)} }
            @keyframes fadeUp { 0%{opacity:0;transform:translateY(8px)} 100%{opacity:1;transform:translateY(0)} }
            @keyframes shine { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
            @keyframes pulseSoft { 0%,100%{opacity:.6} 50%{opacity:1} }
          `}</style>

          {/* Impact IA */}
          <div
            className="relative mb-4 overflow-hidden rounded-3xl border border-black/10 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-white/15 dark:bg-white/[.06]"
            style={{ animation: "fadeUp .6s ease-out both" }}
          >
            {/* liseré animé */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#013486] via-[#F35403] to-[#013486]"
              style={{ backgroundSize: "200% 100%", animation: "shine 4s linear infinite" }}
              aria-hidden
            />
            {/* pastille décorative */}
            <span
              className="absolute -right-3 -top-3 h-10 w-10 rounded-2xl bg-[#F35403]/20"
              style={{ filter: "blur(2px)", animation: "float 6s ease-in-out infinite" }}
              aria-hidden
            />
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: BRAND_ACCENT, animation: "pulseSoft 2.2s ease-in-out infinite" }}
                aria-hidden
              />
              <div className="text-[15px] font-semibold text-[#0b1f4d] dark:text-white">Impact IA</div>
            </div>
            <div className="mt-1 text-sm font-medium text-[#013486] dark:text-white">
              Progrès accélérés. Suivi précis. Décisions éclairées.
            </div>

            {/* micro-indicateurs (kpis) — contraste renforcé */}
            <div className="mt-3 flex flex-wrap gap-2" aria-label="Indicateurs IA">
              <span className="rounded-full border border-[#013486]/30 bg-white/90 px-2.5 py-1 text-[13px] font-medium text-[#0b1f4d] shadow-sm dark:border-white/20 dark:bg-white/[.07] dark:text-white">
                Optimisation des processus
              </span>
              <span className="rounded-full border border-[#013486]/30 bg-white/90 px-2.5 py-1 text-[13px] font-medium text-[#0b1f4d] shadow-sm dark:border-white/20 dark:bg-white/[.07] dark:text-white">
                Indicateurs en temps réel
              </span>
              <span className="rounded-full border border-[#013486]/30 bg-white/90 px-2.5 py-1 text-[13px] font-medium text-[#0b1f4d] shadow-sm dark:border-white/20 dark:bg-white/[.07] dark:text-white">
                Aide à la décision
              </span>
            </div>
          </div>

          {/* Expérience unifiée */}
          <div
            className="relative mb-4 overflow-hidden rounded-3xl border border-black/10 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-white/15 dark:bg-white/[.06]"
            style={{ animation: "fadeUp .6s .05s ease-out both" }}
          >
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: BRAND_PRIMARY, animation: "pulseSoft 2.4s ease-in-out infinite" }}
                aria-hidden
              />
              <div className="text-[15px] font-semibold text-[#0b1f4d] dark:text-white">Expérience unifiée</div>
            </div>
            <div className="mt-1 text-sm text-zinc-700 dark:text-zinc-200">
              Direction • Enseignants • Élèves — un même espace.
            </div>

            {/* rail animé + “avatars” textuels */}
            <div className="mt-3">
              <div
                className="h-1 rounded-full bg-gradient-to-r from-[#013486] via-[#F35403] to-[#013486]"
                style={{ backgroundSize: "200% 100%", animation: "shine 5s linear infinite" }}
                aria-hidden
              />
              <div className="mt-3 flex items-center justify-between text-[11px] text-[#0b1f4d] dark:text-white/85">
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#013486]/30 bg-white/90 font-medium backdrop-blur shadow-sm dark:border-white/20 dark:bg-white/[.07]"
                  style={{ animation: "float 7s ease-in-out infinite" }}
                >
                  Dir.
                </span>
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#013486]/30 bg-white/90 font-medium backdrop-blur shadow-sm dark:border-white/20 dark:bg-white/[.07]"
                  style={{ animation: "float 6s .3s ease-in-out infinite" }}
                >
                  Ens.
                </span>
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#013486]/30 bg-white/90 font-medium backdrop-blur shadow-sm dark:border-white/20 dark:bg-white/[.07]"
                  style={{ animation: "float 8s .6s ease-in-out infinite" }}
                >
                  Él.
                </span>
              </div>
            </div>
          </div>

          {/* Recherche & Développement */}
          <div
            className="relative overflow-hidden rounded-3xl border border-black/10 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-white/15 dark:bg-white/[.06]"
            style={{ animation: "fadeUp .6s .1s ease-out both" }}
          >
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: BRAND_ACCENT, animation: "pulseSoft 2s ease-in-out infinite" }}
                aria-hidden
              />
              <div className="text-[15px] font-semibold text-[#0b1f4d] dark:text-white">Recherche & Développement</div>
            </div>
            <div className="mt-1 text-sm text-zinc-700 dark:text-zinc-200">
              Hypothèses → prototypes → tests sur le terrain → mesure d’impact → itération.
              Partenariats académiques, éthique IA, conformité & sécurité des données.
            </div>

            {/* puces rapides — contraste renforcé */}
            <div className="mt-3 flex flex-wrap gap-2 text-[13px]">
              <span className="rounded-full border border-[#013486]/30 bg-white/90 px-2 py-1 font-medium text-[#0b1f4d] shadow-sm dark:border-white/20 dark:bg-white/[.07] dark:text-white">
                Recherche
              </span>
              <span className="rounded-full border border-[#013486]/30 bg-white/90 px-2 py-1 font-medium text-[#0b1f4d] shadow-sm dark:border-white/20 dark:bg-white/[.07] dark:text-white">
                Développement
              </span>
              <span className="rounded-full border border-[#013486]/30 bg-white/90 px-2 py-1 font-medium text-[#0b1f4d] shadow-sm dark:border-white/20 dark:bg-white/[.07] dark:text-white">
                Évaluation
              </span>
            </div>

            {/* barre de progression R&D en cours */}
            <div className="mt-3">
              <div className="mb-1 flex items-center justify-between text-[12px] text-[#0b1f4d] dark:text-white/85">
                <span>R&D en cours</span>
                <span>Phase pilote</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full border border-[#013486]/25 bg-white/80 dark:border-white/20 dark:bg-white/[.08]">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#013486] via-[#F35403] to-[#013486]"
                  style={{ width: "65%", backgroundSize: "200% 100%", animation: "shine 3.5s linear infinite" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liseré de fin */}
      <div className="h-px w-full bg-black/10 dark:bg-white/10" />
    </section>
  );
}
